import {
	fetchUtils,
	DataProvider,
	GetListParams,
	GetOneParams,
	GetManyParams,
	GetManyReferenceParams,
	CreateParams,
	UpdateParams,
	UpdateManyParams,
	DeleteParams,
	RaRecord,
} from 'react-admin';
import simpleRestProvider from 'ra-data-simple-rest';
import r from '../Utils/fetch';

const w: any = window;

const billRessources = [
	'billing-accounts',
	'billing-account-users',
	'plans',
	'features',
	'plan-features',
	'subscriptions',
	'entitlement-overrides',
];
const connectorRessources = [
	'logs',
	'log-rows',
	'connectors',
	'connector-accounts',
];
const mcpRessources = [
	'mcp-members',
	'mcp-member-configs',
];

// Personnaliser la fonction de requête
const httpClient = (url: string, options: any = {}) => {
	const authToken = localStorage.getItem('token'); // Récupérer le token enregistré

	if (!options.headers) {
		options.headers = new Headers({ Accept: 'application/json' });
	}

	// Ajouter l'en-tête `Authorization` si un token est présent
	if (authToken) {
		options.headers.set('Authorization', `Bearer ${authToken}`);
	}

	return fetchUtils.fetchJson(url, options);
};

// Providers spécifiques pour chaque API
const apiCoreProvider = simpleRestProvider(
	`${w.API_CORE_URL}/v1/admin`,
	httpClient
) as DataProvider;
const billProvider = simpleRestProvider(
	`${w.API_BILLING_URL}/v1/admin`,
	httpClient
) as DataProvider;
// api-connectors-v2: the v1 admin routes were ported (QV2-4910).
const contectorProvider = simpleRestProvider(
	`${w.API_CONNECTORS_CONFIG_URL}/v1/admin`,
	httpClient
) as DataProvider;
const baseMcpProvider = simpleRestProvider(
	`${w.API_MCP_INTERNE_URL}/api`,
	httpClient
) as DataProvider;

// Espace Partenaire (api-partners)
const partnerRessources = [
	'partners',
	'partner-members',
	'commission-rates',
	'opportunities',
	'attributions',
	'commissions',
	'payouts',
	'referrals',
	// Contenu de l'espace partenaire (cas d'usage + leurs thèmes). Oublier une
	// ressource ici l'envoie silencieusement sur api-core, qui répond 404 : le
	// symptôme est un écran vide, pas une erreur explicite.
	'use-cases',
	'use-case-themes',
	// Demandes d'accès en attente d'arbitrage.
	'partner-signup-requests',
];
const partnersProvider = simpleRestProvider(
	`${w.API_PARTNERS_URL}/v1/admin`,
	httpClient
) as DataProvider;

// Wrapper pour mapper les noms de ressources MCP
const mcpResourceMap: Record<string, string> = {
	'mcp-members': 'members',
	'mcp-member-configs': 'configs',
};

const mcpProvider: DataProvider = {
	getList: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.getList(apiResource, params);
	},
	getOne: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.getOne(apiResource, params);
	},
	getMany: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.getMany(apiResource, params);
	},
	getManyReference: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.getManyReference(apiResource, params);
	},
	create: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.create(apiResource, params);
	},
	update: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.update(apiResource, params);
	},
	updateMany: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.updateMany(apiResource, params);
	},
	delete: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.delete(apiResource, params);
	},
	deleteMany: (resource, params) => {
		const apiResource = mcpResourceMap[resource] || resource;
		return baseMcpProvider.deleteMany(apiResource, params);
	},
};

// Provider principal qui redirige en fonction des ressources
const apiProvider: DataProvider = {
	getList: (resource: string, params: GetListParams) => {
		if (billRessources.includes(resource)) {
			return billProvider.getList(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.getList(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.getList(resource, params);
		}
		return apiCoreProvider.getList(resource, params);
	},
	getOne: async (resource: string, params: GetOneParams) => {
		if (resource === 'connector-accounts') {
			const raId = `${params.meta.projectFolderId}-${params.meta.sku}-${params.meta.accountId}`;
			const res: any = await r({
				url: `${w.API_CONNECTORS_CONFIG_URL}/v1/admin/${resource}/${params.meta.projectFolderId}/${params.meta.sku}/${params.meta.accountId}`,
				method: 'GET',
			});

			const data = { data: { ...res, id: raId } };

			return data;
		}

		if (billRessources.includes(resource)) {
			return billProvider.getOne(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.getOne(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.getOne(resource, params);
		}
		return apiCoreProvider.getOne(resource, params);
	},
	getMany: (resource: string, params: GetManyParams) => {
		if (billRessources.includes(resource)) {
			return billProvider.getMany(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.getMany(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.getMany(resource, params);
		}
		return apiCoreProvider.getMany(resource, params);
	},
	getManyReference: (resource: string, params: GetManyReferenceParams) => {
		if (billRessources.includes(resource)) {
			return billProvider.getManyReference(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.getManyReference(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.getManyReference(resource, params);
		}
		return apiCoreProvider.getManyReference(resource, params);
	},
	create: (resource: string, params: CreateParams) => {
		if (billRessources.includes(resource)) {
			return billProvider.create(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.create(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.create(resource, params);
		}
		return apiCoreProvider.create(resource, params);
	},
	update: (resource: string, params: UpdateParams) => {
		if (billRessources.includes(resource)) {
			return billProvider.update(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.update(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.update(resource, params);
		}
		return apiCoreProvider.update(resource, params);
	},
	updateMany: <RecordType extends RaRecord = any>(
		resource: string,
		params: UpdateManyParams<RecordType>
	) => {
		if (billRessources.includes(resource)) {
			return billProvider.updateMany(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.updateMany(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.updateMany(resource, params);
		}
		return apiCoreProvider.updateMany(resource, params);
	},
	delete: (resource: string, params: DeleteParams) => {
		if (billRessources.includes(resource)) {
			return billProvider.delete(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.delete(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.delete(resource, params);
		}
		return apiCoreProvider.delete(resource, params);
	},
	deleteMany: <RecordType extends RaRecord = any>(
		resource: string,
		params: GetManyParams<RecordType>
	) => {
		if (billRessources.includes(resource)) {
			return billProvider.deleteMany(resource, params);
		}
		if (connectorRessources.includes(resource)) {
			return contectorProvider.deleteMany(resource, params);
		}
		if (mcpRessources.includes(resource)) {
			return mcpProvider.deleteMany(resource, params);
		}
		return apiCoreProvider.deleteMany(resource, params);
	},
};

// Redirige les ressources de l'Espace Partenaire vers api-partners, délègue le
// reste au provider multi-API existant.
const routedProvider: DataProvider = new Proxy(apiProvider, {
	get(target: any, prop: string) {
		const original = target[prop];
		if (typeof original !== 'function') return original;
		return (resource: string, params: any) =>
			partnerRessources.includes(resource)
				? (partnersProvider as any)[prop](resource, params)
				: original(resource, params);
	},
}) as DataProvider;

export default routedProvider;
